import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './index.css';
import './shared/styles/accessibility.css';
import App from './App';
import { ToastProvider } from '@uhg-abyss/web/ui/Toast';
import { createTheme } from '@uhg-abyss/web/tools/theme';
import { ThemeProvider } from '@uhg-abyss/web/ui/ThemeProvider';
import { initializeDatadogRum } from './shared/lib/datadog-rum';

// Initialize Datadog RUM before React app renders
initializeDatadogRum();

// Override the UHC theme font tokens to use OptumSans (matching the optumreal-hte-rn app).
// The @font-face declarations for these families live in src/index.css.
const uhcTheme = createTheme('uhc', {
  theme: {
    fonts: {
      'web.core.font-family.uhc2020-sans': 'OptumSans',
      'web.core.font-family.enterprise-sans-vf': 'OptumSans',
      'web.core.font-family.uhc-serif-headline': 'UHCSerifHeadline',
      'web.core.font-family.sf-pro-text-semi-bold': 'SFProTextSemiBold',
      'web.core.font-family.sf-pro-text-regular': 'SFProTextRegular',
    },
  },
});

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('#root element not found in index.html');
}

// One QueryClient at the app root. Questionnaire data isn't a continuous stream that goes stale,
// so default staleTime is Infinity; per-query overrides (e.g. autofill-status polling) handle
// the dynamic cases.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: Infinity,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider theme={uhcTheme}>
          <ToastProvider />
          <App />
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
